import { TEMPLATE_LIMITS } from '../core/constants.js';
import type { Button, GenericTemplateElement, MediaTemplateElement } from '../types/templates.js';

export class TemplateValidationError extends Error {
  constructor(message: string) {
    super(message);
    this.name = 'TemplateValidationError';
  }
}

export function validateGenericTemplate(elements: GenericTemplateElement[]): void {
  if (elements.length === 0) {
    throw new TemplateValidationError('Generic template must have at least 1 element');
  }

  if (elements.length > TEMPLATE_LIMITS.GENERIC_ELEMENTS_MAX) {
    throw new TemplateValidationError(
      `Generic template cannot have more than ${TEMPLATE_LIMITS.GENERIC_ELEMENTS_MAX} elements`
    );
  }

  elements.forEach((element, index) => {
    validateGenericElement(element, index);
  });
}

export function validateGenericElement(element: GenericTemplateElement, index: number): void {
  if (!element.title || element.title.trim() === '') {
    throw new TemplateValidationError(`Element ${index}: title is required`);
  }

  if (element.title.length > TEMPLATE_LIMITS.GENERIC_TITLE_MAX_CHARS) {
    throw new TemplateValidationError(
      `Element ${index}: title cannot exceed ${TEMPLATE_LIMITS.GENERIC_TITLE_MAX_CHARS} characters`
    );
  }

  if (element.subtitle && element.subtitle.length > TEMPLATE_LIMITS.GENERIC_SUBTITLE_MAX_CHARS) {
    throw new TemplateValidationError(
      `Element ${index}: subtitle cannot exceed ${TEMPLATE_LIMITS.GENERIC_SUBTITLE_MAX_CHARS} characters`
    );
  }

  if (element.image_url && !isHttpsUrl(element.image_url)) {
    throw new TemplateValidationError(`Element ${index}: image_url must be HTTPS`);
  }

  if (element.buttons) {
    validateButtons(element.buttons, `Element ${index}`);
  }

  // Validate that element has at least one property beyond title
  const hasAdditionalProperty = !!(
    element.subtitle ||
    element.image_url ||
    element.default_action ||
    (element.buttons && element.buttons.length > 0)
  );

  if (!hasAdditionalProperty) {
    throw new TemplateValidationError(
      `Element ${index}: must have at least one additional property beyond title`
    );
  }
}

export function validateButtonTemplate(text: string, buttons: Button[]): void {
  if (!text || text.trim() === '') {
    throw new TemplateValidationError('Button template text is required');
  }

  if (text.length > TEMPLATE_LIMITS.BUTTON_TEXT_MAX_CHARS) {
    throw new TemplateValidationError(
      `Button template text cannot exceed ${TEMPLATE_LIMITS.BUTTON_TEXT_MAX_CHARS} characters`
    );
  }

  if (buttons.length === 0) {
    throw new TemplateValidationError('Button template must have at least 1 button');
  }

  validateButtons(buttons, 'Button template');
}

export function validateMediaTemplate(element: MediaTemplateElement): void {
  if (!element.media_type) {
    throw new TemplateValidationError('Media template element must have media_type');
  }

  if (!element.url && !element.attachment_id) {
    throw new TemplateValidationError('Media template element must have either url or attachment_id');
  }

  if (element.url && element.attachment_id) {
    throw new TemplateValidationError(
      'Media template element cannot have both url and attachment_id'
    );
  }

  if (element.url && !isHttpsUrl(element.url)) {
    throw new TemplateValidationError('Media template url must be HTTPS');
  }

  if (element.buttons) {
    if (element.buttons.length > TEMPLATE_LIMITS.MEDIA_BUTTONS_MAX_COUNT) {
      throw new TemplateValidationError(
        `Media template cannot have more than ${TEMPLATE_LIMITS.MEDIA_BUTTONS_MAX_COUNT} buttons`
      );
    }
    validateButtons(element.buttons, 'Media template');
  }
}

export function validateButtons(buttons: Button[], context: string): void {
  if (buttons.length > TEMPLATE_LIMITS.BUTTONS_MAX_COUNT) {
    throw new TemplateValidationError(
      `${context} cannot have more than ${TEMPLATE_LIMITS.BUTTONS_MAX_COUNT} buttons`
    );
  }

  buttons.forEach((button, index) => {
    validateButton(button, `${context} button ${index}`);
  });
}

export function validateButton(button: Button, context: string): void {
  if (!button.type) {
    throw new TemplateValidationError(`${context}: type is required`);
  }

  // Title is required for most button types (not for account_unlink)
  if (button.type !== 'account_unlink' && (!button.title || button.title.trim() === '')) {
    throw new TemplateValidationError(`${context}: title is required for ${button.type} buttons`);
  }

  if (button.title && button.title.length > TEMPLATE_LIMITS.BUTTON_TITLE_MAX_CHARS) {
    throw new TemplateValidationError(
      `${context}: title cannot exceed ${TEMPLATE_LIMITS.BUTTON_TITLE_MAX_CHARS} characters`
    );
  }

  // Type-specific validations
  switch (button.type) {
    case 'web_url':
      if (!button.url) {
        throw new TemplateValidationError(`${context}: url is required for web_url buttons`);
      }
      if (!isHttpsUrl(button.url)) {
        throw new TemplateValidationError(`${context}: url must be HTTPS for web_url buttons`);
      }
      break;

    case 'postback':
      if (!button.payload) {
        throw new TemplateValidationError(`${context}: payload is required for postback buttons`);
      }
      if (button.payload.length > TEMPLATE_LIMITS.POSTBACK_PAYLOAD_MAX_CHARS) {
        throw new TemplateValidationError(
          `${context}: payload cannot exceed ${TEMPLATE_LIMITS.POSTBACK_PAYLOAD_MAX_CHARS} characters`
        );
      }
      break;

    case 'phone_number':
      if (!button.payload) {
        throw new TemplateValidationError(`${context}: payload is required for phone_number buttons`);
      }
      // Basic phone number validation (starts with +)
      if (!button.payload.startsWith('+')) {
        throw new TemplateValidationError(
          `${context}: phone_number payload must start with + (e.g., +1234567890)`
        );
      }
      break;

    case 'game_play':
      // game_play buttons may have optional game_metadata
      break;

    case 'account_link':
      if (!button.url) {
        throw new TemplateValidationError(`${context}: url is required for account_link buttons`);
      }
      if (!isHttpsUrl(button.url)) {
        throw new TemplateValidationError(`${context}: url must be HTTPS for account_link buttons`);
      }
      break;

    case 'account_unlink':
      // account_unlink buttons don't require additional properties
      break;
  }

  // Validate webview properties for web_url buttons
  if (button.type === 'web_url' && button.messenger_extensions && button.fallback_url) {
    if (!isHttpsUrl(button.fallback_url)) {
      throw new TemplateValidationError(`${context}: fallback_url must be HTTPS`);
    }
  }
}

function isHttpsUrl(url: string): boolean {
  try {
    const parsedUrl = new URL(url);
    return parsedUrl.protocol === 'https:';
  } catch {
    return false;
  }
}